import { useQueryResults } from "@/hooks/useQueryResults";
import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { DatasetAreaChart, DatasetSource } from "./charts/dataset-area-chart";
import { DatasetLineChart } from "./charts/dataset-line-chart";

export function LockstakeSkyYield() {
    const { results } = useQueryResults({id: 6854509})

    const dataset = useMemo(() => {
        const series = ['date', 'apy']

        const data = results.map(r => ([r.date.toDate(), r.apy]))

        return {
            dimensions: series,
            source: data
        }
    }, [results])

    const percentFormatter = new Intl.NumberFormat("en", {style: 'percent', minimumFractionDigits: 2})
    const axisFormatter = new Intl.NumberFormat("en", {style: 'percent', maximumFractionDigits: 0})

    return <div className="h-[40vh]">
        <DatasetLineChart
            title="Sky/SKY Yield lockstake"
            dataset={dataset}
            yAxisFormatter={axisFormatter.format}
            tooltip={{seriesValueFormatter: (v) => `${percentFormatter.format(v)}`}}
        />
    </div>
}

export function LockstakeSkyRestaking() {
    const { results } = useQueryResults({id: 6138270})
    const numberFormatter = new Intl.NumberFormat("en", {maximumFractionDigits: 0})
    const percentFormatter = new Intl.NumberFormat("en", {style: 'percent', minimumFractionDigits: 2})
    const axisFormatter = new Intl.NumberFormat("en", {notation: 'compact'})
    const totalClaims = useMemo(() => {
        if (!results[0]) return 0;
        return results[0].claims_alltime as number;
    }, [results])

    const restakingAlltime = useMemo(() => {
        if (!results[0]) return 0;
        return results[0].restaking_index_alltime as number;
    }, [results])

    const restakedTotal = useMemo(() => {
        if (!results[0]) return 0;
        return results[0].stakes_within_day_alltime as number;
    }, [results])

    const totalClaims_30d = useMemo(() => {
        if (!results[0]) return 0;
        return results[0].claims_30d as number;
    }, [results])

    const restaking_30d = useMemo(() => {
        if (!results[0]) return 0;
        return results[0].restaking_index_30d as number;
    }, [results])

    const restakedTotal_30d = useMemo(() => {
        if (!results[0]) return 0;
        return results[0].stakes_within_day_30d as number;
    }, [results])

    const activity: DatasetSource = useMemo(() => {
        let ds: DatasetSource = {
            dimensions: ['Date', 'Claimed', 'Restaked'],
            source: [[]]
        }

        if (results)
            ds.source = results.map(r => [r.date!.toDate(), r.claims!, r.stakes_within_day!])

        return ds
    }, [results])

    const claimsOverTime: DatasetSource = useMemo(() => {
        let ds: DatasetSource = {
            dimensions: ['Date', 'Total Claimed', 'Total Restaked'],
            source: [[]]
        }

        if (results)
            ds.source = results.map(r => [r.date!.toDate(), r.claims_alltime!, r.stakes_within_day_alltime!])

        return ds
    }, [results])

    return <div className='flex flex-col gap-4'>
        <div className="flex flex-row">
            <span className="block text-2xl lg:ml-8 ml-4 grow">Lockstake SKY/SKY Restaking</span>
            <span className="block text-xs place-self-end">Restaking is only counted when a stake is made within 24h of a claim</span>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 text-center">
            <Card>
                <CardHeader>
                    <CardTitle>Total claims</CardTitle>
                </CardHeader>
                <CardContent>{numberFormatter.format(totalClaims)} SKY</CardContent>
            </Card>
            <Card>
            <CardHeader>
                <CardTitle>Overall Restaking Index</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="relative h-8 flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded overflow-hidden">
                        <div
                            className="absolute inset-y-0 left-0 transition-all duration-300"
                            style={{
                                width: `${Math.min(restakingAlltime * 100, 100)}%`,
                                backgroundColor: restakingAlltime > 0.75 ? '#22c55e' : restakingAlltime > 0.4 ? '#eab308' : '#ef4444'
                            }}
                        />
                        <span className="relative z-10 font-semibold">{percentFormatter.format(restakingAlltime)}</span>
                    </div>
                </CardContent>
            </Card>
            <Card>
            <CardHeader>
                <CardTitle>Total Restaked</CardTitle>
                </CardHeader>
                <CardContent>{numberFormatter.format(restakedTotal)} SKY</CardContent>
            </Card>
            <Card>
                <CardHeader>
                    <CardTitle>Total claims (last 30 days)</CardTitle>
                </CardHeader>
                <CardContent>{numberFormatter.format(totalClaims_30d)} SKY</CardContent>
            </Card>
            <Card>
            <CardHeader>
                <CardTitle>Restaking Index (last 30 days)</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="relative h-8 flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded overflow-hidden">
                        <div
                            className="absolute inset-y-0 left-0 transition-all duration-300"
                            style={{
                                width: `${Math.min(restaking_30d * 100, 100)}%`,
                                backgroundColor: restaking_30d > 0.75 ? '#22c55e' : restaking_30d > 0.4 ? '#eab308' : '#ef4444'
                            }}
                        />
                        <span className="relative z-10 font-semibold">{percentFormatter.format(restaking_30d)}</span>
                    </div>
                </CardContent>
            </Card>
            <Card>
            <CardHeader>
                <CardTitle>Total Restaked (last 30 days)</CardTitle>
                </CardHeader>
                <CardContent>{numberFormatter.format(restakedTotal_30d)} SKY</CardContent>
            </Card>
        </div>
        <div className="h-[40vh]">
            <DatasetAreaChart
                dataset={activity}
                yAxisFormatter={axisFormatter.format}
                tooltip={{seriesValueFormatter: (v) => `${numberFormatter.format(v)} SKY`}}
                stack={false}
            />
        </div>
        <div className="h-[40vh]">
            <DatasetAreaChart 
                dataset={claimsOverTime}
                yAxisFormatter={axisFormatter.format}
                tooltip={{seriesValueFormatter: (v) => `${numberFormatter.format(v)} SKY`}}
                stack={false}
            />
        </div>
    </div>
}
